import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Loader2, Plus, Check, Clock, AlertTriangle, Milestone,
  ClipboardList, CalendarClock, Star, Trash2, Bell, BookOpen, Link2, Users, CalendarDays
} from 'lucide-react';
import { toast } from 'sonner';
import ContactSelect from '@/components/ContactSelect';
import AgentActivityPanel from '@/components/AgentActivityPanel';

const TYPE_CONFIG = {
  task:      { icon: ClipboardList, label: 'Task', color: 'bg-blue-100 text-blue-700' },
  reminder:  { icon: Bell, label: 'Reminder', color: 'bg-amber-100 text-amber-700' },
  follow_up: { icon: CalendarClock, label: 'Follow-up', color: 'bg-violet-100 text-violet-700' },
  decision:  { icon: AlertTriangle, label: 'Decisione', color: 'bg-red-100 text-red-700' },
  milestone: { icon: Milestone, label: 'Milestone', color: 'bg-emerald-100 text-emerald-700' },
  log:       { icon: BookOpen, label: 'Log', color: 'bg-slate-100 text-slate-600' },
};

const PRIORITY_CONFIG = {
  high:   { label: 'Alta', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  medium: { label: 'Media', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  low:    { label: 'Bassa', color: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400' },
};

function AgendaItem({ item, onComplete, onSnooze, onDelete }) {
  const navigate = useNavigate();
  const typeConf = TYPE_CONFIG[item.item_type] || TYPE_CONFIG.task;
  const prioConf = PRIORITY_CONFIG[item.priority] || PRIORITY_CONFIG.medium;
  const TypeIcon = typeConf.icon;
  const isLog = item.item_type === 'log';
  const isDone = item.status === 'done';
  const isOverdue = item.due_date && item.due_date < new Date().toISOString().slice(0, 10) && !isDone;

  return (
    <div className={`p-3 rounded-lg border transition-all ${isDone ? 'border-[#E2E8F0] bg-[#F8FAFC] opacity-60' : isOverdue ? 'border-red-200 bg-red-50/30' : 'border-[#E2E8F0] bg-white hover:shadow-sm'}`}
      data-testid={`agenda-item-${item.id}`}>
      <div className="flex items-start gap-2.5">
        {!isLog && !isDone && (
          <button onClick={() => onComplete(item.id)}
            className="mt-0.5 w-4.5 h-4.5 rounded-full border-2 border-[#CBD5E1] hover:border-emerald-500 hover:bg-emerald-50 transition-colors flex-shrink-0"
            data-testid={`complete-${item.id}`} />
        )}
        {isDone && <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />}
        {isLog && <BookOpen className="w-4 h-4 text-[#94A3B8] flex-shrink-0 mt-0.5" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`text-[11px] font-medium ${isDone ? 'line-through text-[#94A3B8]' : 'text-[#1A3A5C]'}`}>{item.title}</span>
            <Badge className={`text-[8px] ${typeConf.color} px-1 py-0`}>{typeConf.label}</Badge>
            <div className={`w-1.5 h-1.5 rounded-full ${prioConf.dot}`} />
          </div>
          {item.description && (
            <p className="text-[10px] text-[#64748B] mt-0.5 line-clamp-2">{item.description}</p>
          )}
          {/* Linked entity — clickable */}
          {item.linked_entity && (
            <button onClick={(e) => {
              e.stopPropagation();
              if (item.linked_entity.type === 'contact') navigate(`/contacts?open=${item.linked_entity.id}`);
              else if (item.linked_entity.type === 'event') navigate(`/events?open=${item.linked_entity.id}`);
            }}
              className="mt-1 flex items-center gap-1 text-[9px] text-[#2E75B6] hover:underline"
              data-testid={`agenda-link-${item.id}`}>
              {item.linked_entity.type === 'contact' ? <Users className="w-2.5 h-2.5" /> : <CalendarDays className="w-2.5 h-2.5" />}
              {item.linked_entity.name}
            </button>
          )}
          <div className="flex items-center gap-2 mt-1">
            {item.due_date && (
              <span className={`text-[9px] ${isOverdue ? 'text-red-600 font-semibold' : 'text-[#94A3B8]'}`}>
                {isOverdue ? 'Scaduto: ' : ''}{item.due_date}
              </span>
            )}
            {item.source_agent && <span className="text-[9px] text-[#CBD5E1]">{item.source_agent}</span>}
            {item.tags?.length > 0 && item.tags.map(t => (
              <Badge key={t} className="text-[7px] bg-[#F1F5F9] text-[#64748B] px-1 py-0">{t}</Badge>
            ))}
          </div>
        </div>
        {!isDone && !isLog && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={() => onSnooze(item.id)}
              className="p-1 rounded hover:bg-[#F1F5F9] text-[#94A3B8] hover:text-[#64748B] transition-colors"
              title="Snooze +7gg" data-testid={`snooze-${item.id}`}>
              <Clock className="w-3 h-3" />
            </button>
            <button onClick={() => onDelete(item.id)}
              className="p-1 rounded hover:bg-red-50 text-[#94A3B8] hover:text-red-500 transition-colors"
              title="Elimina" data-testid={`delete-${item.id}`}>
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CreateDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [itemType, setItemType] = useState('task');
  const [priority, setPriority] = useState('medium');
  const [dueDate, setDueDate] = useState('');
  const [linkType, setLinkType] = useState('');
  const [linkedContact, setLinkedContact] = useState(null);
  const [eventsList, setEventsList] = useState([]);
  const [linkedEventId, setLinkedEventId] = useState('');

  useEffect(() => {
    if (open) {
      api.get('/events').then(r => {
        const upcoming = (r.data || []).filter(e =>
          e.status !== 'cancelled'
        ).slice(0, 30);
        setEventsList(upcoming);
      }).catch(() => {});
    }
  }, [open]);

  const getLinkedEntity = () => {
    if (linkType === 'contact' && linkedContact) return { type: 'contact', id: linkedContact.id, name: linkedContact.name };
    if (linkType === 'event' && linkedEventId) {
      const ev = eventsList.find(e => e.id === linkedEventId);
      return ev ? { type: 'event', id: ev.id, name: ev.name } : null;
    }
    return null;
  };

  const handleCreate = async () => {
    if (!title.trim()) return;
    try {
      await api.post('/agenda', {
        title: title.trim(), description: desc, item_type: itemType,
        priority, due_date: dueDate || null,
        linked_entity: getLinkedEntity(),
      });
      toast.success('Item aggiunto');
      setTitle(''); setDesc(''); setDueDate(''); setLinkType(''); setLinkedContact(null); setLinkedEventId('');
      setOpen(false);
      onCreated();
    } catch {
      toast.error('Errore creazione');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-[#1A3A5C] hover:bg-[#2E75B6] text-xs h-8" data-testid="create-agenda-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Nuovo Item
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-sm font-bold text-[#1A3A5C]">Nuovo Item Agenda</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <Input placeholder="Titolo (max 100 car.)" value={title} onChange={e => setTitle(e.target.value.slice(0, 100))} className="text-xs" data-testid="agenda-title-input" />
          <Input placeholder="Descrizione (opzionale)" value={desc} onChange={e => setDesc(e.target.value)} className="text-xs" data-testid="agenda-desc-input" />
          <div className="grid grid-cols-3 gap-2">
            <Select value={itemType} onValueChange={setItemType}>
              <SelectTrigger className="text-xs h-8" data-testid="agenda-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="task">Task</SelectItem>
                <SelectItem value="reminder">Reminder</SelectItem>
                <SelectItem value="follow_up">Follow-up</SelectItem>
                <SelectItem value="decision">Decisione</SelectItem>
              </SelectContent>
            </Select>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger className="text-xs h-8" data-testid="agenda-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="high">Alta</SelectItem>
                <SelectItem value="medium">Media</SelectItem>
                <SelectItem value="low">Bassa</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="text-xs h-8" data-testid="agenda-due-input" />
          </div>
          {/* Linked entity selector */}
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1"><Link2 className="w-3 h-3" />Collegamento</Label>
            <Select value={linkType} onValueChange={(v) => { setLinkType(v); setLinkedContact(null); setLinkedEventId(''); }}>
              <SelectTrigger className="text-xs h-8" data-testid="agenda-link-type"><SelectValue placeholder="Nessun collegamento" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nessun collegamento</SelectItem>
                <SelectItem value="contact">Contatto CRM</SelectItem>
                <SelectItem value="event">Evento</SelectItem>
              </SelectContent>
            </Select>
            {linkType === 'contact' && (
              <ContactSelect value={linkedContact} onChange={setLinkedContact} placeholder="Cerca contatto nel CRM..." />
            )}
            {linkType === 'event' && (
              <select value={linkedEventId} onChange={e => setLinkedEventId(e.target.value)}
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-xs" data-testid="agenda-link-event">
                <option value="">Seleziona evento...</option>
                {eventsList.map(ev => (
                  <option key={ev.id} value={ev.id}>{ev.name} — {ev.event_date?.slice(0, 10)}</option>
                ))}
              </select>
            )}
          </div>
          {/* Follow-up suggestion */}
          {itemType === 'follow_up' && linkType !== 'contact' && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-2">
              <p className="text-[10px] text-amber-700">Suggerimento: collega questo follow-up a un contatto CRM per tracciare la relazione.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button size="sm" className="bg-[#1A3A5C] hover:bg-[#2E75B6] text-xs" onClick={handleCreate} data-testid="agenda-submit-btn">Crea</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const FILTER_TYPES = [
  { value: '', label: 'Tutti' },
  { value: 'task', label: 'Task' },
  { value: 'reminder', label: 'Reminder' },
  { value: 'decision', label: 'Decisioni' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'log', label: 'Log' },
];

export default function AgendaPage() {
  const [today, setToday] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [backlog, setBacklog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(() => {
    try { return localStorage.getItem('agenda_filter') || ''; } catch { return ''; }
  });

  const fetchAll = useCallback(async () => {
    try {
      const [todayRes, upcomingRes, allRes] = await Promise.all([
        api.get('/agenda/today'),
        api.get('/agenda/upcoming'),
        api.get('/agenda?status=open'),
      ]);
      setToday(todayRes.data);
      setUpcoming(upcomingRes.data);
      // Backlog = open items with no due_date or future due_date beyond 7 days
      const todayIds = new Set(todayRes.data.map(i => i.id));
      const upcomingIds = new Set(upcomingRes.data.map(i => i.id));
      setBacklog(allRes.data.filter(i => !todayIds.has(i.id) && !upcomingIds.has(i.id)));
    } catch {
      toast.error('Errore caricamento agenda');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleComplete = async (id) => {
    try {
      await api.patch(`/agenda/${id}`, { status: 'done' });
      toast.success('Completato');
      fetchAll();
    } catch { toast.error('Errore'); }
  };

  const handleSnooze = async (id) => {
    try {
      await api.patch(`/agenda/${id}`, { snooze_days: 7 });
      toast.success('Posticipato di 7 giorni');
      fetchAll();
    } catch { toast.error('Errore'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/agenda/${id}`);
      toast.success('Eliminato');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const filterItems = (items) => filter ? items.filter(i => i.item_type === filter) : items;

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="agenda-loading">
      <Loader2 className="w-6 h-6 animate-spin text-[#2E75B6]" />
    </div>
  );

  const columns = [
    { title: 'Oggi', icon: AlertTriangle, items: filterItems(today), color: 'text-red-600', emptyMsg: 'Nessun item per oggi' },
    { title: 'Questa settimana', icon: CalendarClock, items: filterItems(upcoming), color: 'text-amber-600', emptyMsg: 'Nessun item in programma' },
    { title: 'Backlog', icon: ClipboardList, items: filterItems(backlog), color: 'text-[#64748B]', emptyMsg: 'Nessun item nel backlog' },
  ];

  return (
    <div className="space-y-5" data-testid="agenda-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#1A3A5C]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Agenda Operativa
          </h1>
          <p className="text-xs text-[#64748B] mt-0.5">Task, reminder, decisioni e follow-up condivisi con gli agenti</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-[#F1F5F9] rounded-lg p-0.5">
            {FILTER_TYPES.map(f => (
              <button key={f.value} onClick={() => { setFilter(f.value); try { localStorage.setItem('agenda_filter', f.value); } catch {} }}
                className={`px-2 py-1 rounded text-[10px] font-medium transition-colors ${filter === f.value ? 'bg-white text-[#1A3A5C] shadow-sm' : 'text-[#64748B] hover:text-[#1A3A5C]'}`}
                data-testid={`filter-${f.value || 'all'}`}>
                {f.label}
              </button>
            ))}
          </div>
          <CreateDialog onCreated={fetchAll} />
        </div>
      </div>

      <AgentActivityPanel
        agents={['strategist_agent', 'planner_agent', 'crm_agent']}
        areaLabel="Attivita agenti - Agenda"
      />

      {/* 3-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {columns.map(col => (
          <Card key={col.title} className="border-[#E2E8F0]">
            <CardContent className="p-4">
              <h3 className={`text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-1.5 ${col.color}`}>
                <col.icon className="w-3.5 h-3.5" /> {col.title}
                {col.items.length > 0 && <Badge className="bg-[#1A3A5C]/10 text-[#1A3A5C] text-[10px] ml-1">{col.items.length}</Badge>}
              </h3>
              {col.items.length === 0 ? (
                <p className="text-[11px] text-[#94A3B8] text-center py-6">{col.emptyMsg}</p>
              ) : (
                <div className="space-y-2">
                  {col.items.map(item => (
                    <AgendaItem key={item.id} item={item}
                      onComplete={handleComplete} onSnooze={handleSnooze} onDelete={handleDelete} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
